import React, { useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { Box, Typography, Avatar, Button, Skeleton, Divider, Paper, Chip } from "@mui/material";
import { UserPlus, MessageCircle, MapPin, Briefcase, GraduationCap, Users, X, Check } from "lucide-react";
import axios from "axios";

const COMMUNITY_API = "https://apiweb.hitpoly.com/ajax/communityWidgetsController.php";
const RELATIONS_API = "https://apiweb.hitpoly.com/ajax/RelacionesControlador.php";

// Cache para no llamar repetidamente al API
const PROFILE_CACHE = {};

const PersonHoverCard = ({ person, anchorEl, onClose, currentUserId, onNavigateMessage, onNavigateProfile, relationStatus, onFriendAction, onMouseEnter, onMouseLeave }) => {
  const personId = person?.id;
  const [profile, setProfile] = useState(person || null);
  const [loading, setLoading] = useState(true);
  const cardRef = useRef(null);

  useEffect(() => {
    if (!personId || !anchorEl) return;

    // Si ya lo tenemos en cache, usarlo
    if (PROFILE_CACHE[personId]) {
      setProfile(PROFILE_CACHE[personId]);
      setLoading(false);
      return;
    }

    setLoading(true);
    axios.post(COMMUNITY_API, {
      action: "getMiniProfile",
      target_id: personId
    })
      .then(res => {
        if (res.data?.success && res.data.data) {
          const fullProfile = { ...person, ...res.data.data };
          console.log('[PersonHoverCard] full profile data:', fullProfile);
          PROFILE_CACHE[personId] = fullProfile;
          setProfile(fullProfile);
        }
      })
      .catch((err) => {
      })
      .finally(() => setLoading(false));
  }, [personId, anchorEl]);

  // Cerrar al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Estado para posición vertical ajustada después de medir
  const [adjustedTop, setAdjustedTop] = useState(0);

  useEffect(() => {
    if (!loading && cardRef.current && anchorEl) {
      const rect = anchorEl.getBoundingClientRect();
      const actualHeight = cardRef.current.offsetHeight;
      let newTop = rect.top;
      if (newTop + actualHeight > window.innerHeight - 10) {
        newTop = Math.max(10, window.innerHeight - actualHeight - 10);
      }
      if (newTop < 10) newTop = 10;
      setAdjustedTop(newTop);
    }
  }, [loading, profile, anchorEl]);

  if (!anchorEl) return null;

  // Calcular posición al costado
  const rect = anchorEl.getBoundingClientRect();
  const popupWidth = 320;

  // Por defecto, a la derecha
  let left = rect.right + 12;
  // Si no cabe a la derecha, lo ponemos a la izquierda
  if (left + popupWidth > window.innerWidth - 10) {
    left = rect.left - popupWidth - 12;
  }

  const top = adjustedTop || rect.top;

  const fullName = profile ? (profile.nombre && profile.apellido ? `${profile.nombre} ${profile.apellido}`.trim() : (profile.name || profile.nombre || '')) : '';
  const location = profile ? [profile.city, profile.country].filter(Boolean).join(', ') : '';
  const headline = profile?.headline || profile?.cargo_nombre || '';
  const bio = profile?.bio || '';
  const personalStatus = profile?.personal_status || '';
  const interestsArr = profile?.interests ? profile.interests.split('||').filter(Boolean) : [];
  const languagesArr = profile?.languages ? profile.languages.split('||').filter(Boolean).map(l => {
    const parts = l.split('|');
    return { idioma: parts[0] || '', nivel: parts[1] || '' };
  }) : [];

  const renderRelationButton = () => {
    if (!relationStatus || relationStatus.status === "none") {
      return (
        <Button
          variant="contained"
          size="small"
          startIcon={<UserPlus size={14} />}
          onClick={(e) => { e.stopPropagation(); onFriendAction?.("enviar"); }}
          sx={{
            flex: 1,
            textTransform: 'none',
            borderRadius: 3,
            fontWeight: 700,
            fontSize: '0.8rem',
            py: 0.8,
            boxShadow: 'none',
            bgcolor: '#0a66c2',
            '&:hover': { bgcolor: '#004182', boxShadow: '0 2px 8px rgba(10,102,194,0.3)' }
          }}
        >
          Conectar
        </Button>
      );
    }

    if (relationStatus.status === "pending") {
      if (relationStatus.isSender) {
        return (
          <Button
            variant="outlined"
            size="small"
            startIcon={<Check size={14} />}
            onClick={(e) => { e.stopPropagation(); onFriendAction?.("eliminar"); }}
            sx={{ flex: 1, textTransform: 'none', borderRadius: 3, fontWeight: 700, fontSize: '0.8rem', py: 0.8, borderColor: '#0a66c2', color: '#0a66c2' }}
          >
            Conectado
          </Button>
        );
      }
      return (
        <Box sx={{ display: 'flex', gap: 1, flex: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<Check size={14} />}
            onClick={(e) => { e.stopPropagation(); onFriendAction?.("aceptar"); }}
            sx={{ flex: 1, textTransform: 'none', borderRadius: 3, fontWeight: 700, fontSize: '0.8rem', py: 0.8, boxShadow: 'none', bgcolor: '#0a66c2' }}
          >
            Aceptar
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<X size={14} />}
            onClick={(e) => { e.stopPropagation(); onFriendAction?.("eliminar"); }}
            sx={{ flex: 1, textTransform: 'none', borderRadius: 3, fontWeight: 700, fontSize: '0.8rem', py: 0.8, borderColor: '#e0e0e0', color: '#666' }}
          >
            Rechazar
          </Button>
        </Box>
      );
    }

    if (relationStatus.status === "accepted") {
      return (
        <Button
          variant="outlined"
          size="small"
          startIcon={<Users size={14} />}
          disabled
          sx={{ flex: 1, textTransform: 'none', borderRadius: 3, fontWeight: 700, fontSize: '0.8rem', py: 0.8, borderColor: '#0a66c2', color: '#0a66c2' }}
        >
          Conectados
        </Button>
      );
    }

    return null;
  };

  return ReactDOM.createPortal(
    <Paper
      ref={cardRef}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      elevation={8}
      sx={{
        position: 'fixed',
        left,
        top,
        width: popupWidth,
        zIndex: 2147483647,
        borderRadius: 4,
        overflow: 'hidden',
        animation: 'fadeInUp 0.2s ease',
        '@keyframes fadeInUp': {
          from: { opacity: 0, transform: 'translateY(8px)' },
          to: { opacity: 1, transform: 'translateY(0)' }
        }
      }}
    >
      {loading ? (
        <Box sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Skeleton variant="circular" width={64} height={64} />
            <Box sx={{ flex: 1 }}>
              <Skeleton width="80%" height={22} />
              <Skeleton width="60%" height={16} sx={{ mt: 0.5 }} />
              <Skeleton width="40%" height={14} sx={{ mt: 0.5 }} />
            </Box>
          </Box>
          <Skeleton width="100%" height={36} />
        </Box>
      ) : profile ? (
        <>
          {/* Banner */}
          <Box sx={{
            height: 160,
            background: profile.cover_photo
              ? `url(${profile.cover_photo}) center/cover no-repeat`
              : 'linear-gradient(135deg, #0a66c2 0%, #004182 50%, #1a1a2e 100%)',
            position: 'relative'
          }}>
            {profile.cover_photo && (
              <Box sx={{
                position: 'absolute', inset: 0,
                background: 'linear-gradient(0deg, rgba(0,0,0,0.4) 0%, transparent 60%)',
              }} />
            )}
          </Box>

          {/* Avatar superpuesto */}
          <Box sx={{ px: 2.5, mt: -4.5 }}>
            <Avatar
              src={profile.avatar}
              sx={{
                width: 72,
                height: 72,
                border: '3px solid #fff',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                bgcolor: '#e0e0e0',
                color: '#0a66c2',
                fontWeight: 'bold',
                fontSize: '1.5rem',
                cursor: 'pointer'
              }}
              onClick={() => onNavigateProfile?.()}
            >
              {(profile.nombre || '').charAt(0)}
            </Avatar>
          </Box>

          {/* Info principal */}
          <Box sx={{ px: 2.5, pt: 1, pb: 2 }}>
            <Typography
              variant="subtitle1"
              fontWeight={800}
              sx={{ color: '#1a1a1a', lineHeight: 1.3, cursor: 'pointer', '&:hover': { color: '#0a66c2' } }}
              onClick={() => onNavigateProfile?.()}
            >
              {fullName || 'Usuario'}
            </Typography>

            {headline && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3, fontWeight: 500, lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                {headline}
              </Typography>
            )}

            {(location || personalStatus) && (
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: '#666', fontWeight: 500 }}>
                {location && <><MapPin size={12} /> {location}</>}
                {location && personalStatus && <Box component="span" sx={{ mx: 0.3 }}>·</Box>}
                {personalStatus}
              </Typography>
            )}

            {/* Contador amigos */}
            {profile.friend_count > 0 && (
              <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, color: '#0a66c2', fontWeight: 600 }}>
                <Users size={12} /> {profile.friend_count} {profile.friend_count === 1 ? 'conexión' : 'conexiones'}
              </Typography>
            )}

            {/* Intereses */}
            {interestsArr.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', mt: 1 }}>
                {interestsArr.slice(0, 5).map((interest, i) => (
                  <Chip key={i} label={interest} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#eef3f8', color: '#0a66c2', '& .MuiChip-label': { px: 0.8 } }} />
                ))}
              </Box>
            )}

            {/* Idiomas */}
            {languagesArr.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', mt: 0.5 }}>
                {languagesArr.slice(0, 3).map((lang, i) => (
                  <Chip key={i} label={`${lang.idioma}${lang.nivel ? ` (${lang.nivel})` : ''}`} size="small" sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#f0fdf4', color: '#16a34a', '& .MuiChip-label': { px: 0.8 } }} />
                ))}
              </Box>
            )}

            {/* Experiencia laboral */}
            {profile.experience && profile.experience.length > 0 && (
              <Box sx={{ mt: 1.5 }}>
                <Divider sx={{ mb: 1 }} />
                {profile.experience.map((exp, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.8 }}>
                    <Briefcase size={14} color="#666" style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="caption" fontWeight={700} color="#1a1a1a" sx={{ lineHeight: 1.2 }}>
                        {exp.position}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
                        {exp.company} {exp.is_current ? '· Actual' : ''}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Educación */}
            {profile.education && profile.education.length > 0 && (
              <Box sx={{ mt: profile.experience?.length > 0 ? 0.5 : 1.5 }}>
                {!profile.experience?.length && <Divider sx={{ mb: 1 }} />}
                {profile.education.map((edu, i) => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.8 }}>
                    <GraduationCap size={14} color="#666" style={{ marginTop: 2, flexShrink: 0 }} />
                    <Box>
                      <Typography variant="caption" fontWeight={700} color="#1a1a1a" sx={{ lineHeight: 1.2 }}>
                        {edu.institution}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.68rem' }}>
                        {[edu.degree, edu.field_of_study].filter(Boolean).join(' · ')}
                      </Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            )}

            {/* Bio */}
            {bio && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="caption" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden', lineHeight: 1.4 }}>
                  {bio}
                </Typography>
              </>
            )}

            {/* Botones de acción */}
            {Number(personId) !== Number(currentUserId) && (
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                {renderRelationButton()}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MessageCircle size={14} />}
                  onClick={(e) => { e.stopPropagation(); onNavigateMessage?.(); }}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    borderRadius: 3,
                    fontWeight: 700,
                    fontSize: '0.8rem',
                    py: 0.8,
                    borderColor: '#0a66c2',
                    color: '#0a66c2',
                    '&:hover': { bgcolor: '#eef3f8' }
                  }}
                >
                  Mensaje
                </Button>
              </Box>
            )}
          </Box>
        </>
      ) : (
        <Box sx={{ p: 2.5, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">No se pudo cargar el perfil</Typography>
        </Box>
      )}
    </Paper>,
    document.body
  );
};

export default PersonHoverCard;
